import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Eye, Share2, Calendar } from "lucide-react";
import { toast } from "sonner";
import { MediaCard } from "../components/MediaCard";
import { Reveal } from "../components/Reveal";
import { EmptyState } from "../components/EmptyState";
import { AdSlot } from "../components/Ads";
import { VideoPlayer } from "../components/VideoPlayer";
import { LikeButton } from "../components/LikeButton";
import { Comments } from "../components/Comments";
import { http, asList, fileUrl, fmtViews } from "../lib/api";

export default function Watch() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [missing, setMissing] = useState(false);
  const [related, setRelated] = useState([]);
  const counted = useRef("");

  useEffect(() => {
    setItem(null);
    setMissing(false);
    window.scrollTo(0, 0);
    http.get(`/media/${id}`).then((r) => setItem(r.data)).catch(() => setMissing(true));
    http.get("/media", { params: { type: "video", exclude: id, sort: "popular", limit: 6 } }).then((r) => setRelated(asList(r))).catch(() => {});
  }, [id]);

  const onPlay = () => {
    if (counted.current === id) return;
    counted.current = id;
    http.post(`/media/${id}/view`).then((r) => setItem((it) => (it ? { ...it, views: r.data?.views ?? it.views } : it))).catch(() => {});
  };

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copiado");
    } catch {
      toast.error("Não foi possível copiar o link");
    }
  };

  if (missing) return <div className="mx-auto max-w-[1440px] px-5 pt-36 sm:px-10"><EmptyState testid="video-missing" text="Este vídeo não está disponível." /></div>;

  return (
    <div className="mx-auto max-w-[1440px] px-5 pt-28 sm:px-10" data-testid="watch-page">
      <div className="relative bg-black" data-testid="video-player-container">
        <div className="pointer-events-none absolute -inset-10 -z-10 bg-rose-700/10 blur-3xl" />
        {item && (
          <VideoPlayer key={item.id} id={item.id} src={fileUrl(item.id)} poster={item.has_thumb ? fileUrl(item.id, "thumb") : undefined} onPlay={onPlay} />
        )}
        {!item && <div className="aspect-video w-full animate-pulse bg-white/5" />}
      </div>
      <AdSlot placement="video_below" className="mt-8" />

      {item && (
        <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_320px]">
          <div>
            <p className="eyebrow mb-3">{item.category || "Exclusivo"}</p>
            <h1 className="font-serif-d text-3xl font-semibold leading-tight tracking-tight text-white sm:text-5xl" data-testid="video-title">{item.title}</h1>
            <div className="mt-6 flex flex-wrap items-center gap-6 font-mono-d text-xs text-slate-300">
              <span className="flex items-center gap-2" data-testid="video-views"><Eye size={14} /> {fmtViews(item.views)} visualizações</span>
              <span className="flex items-center gap-2"><Calendar size={14} /> {new Date(item.created_at).toLocaleDateString("pt-BR")}</span>
              <button onClick={share} className="flex items-center gap-2 uppercase tracking-widest hover:text-rose-400" data-testid="share-button"><Share2 size={14} /> Compartilhar</button>
              <LikeButton id={item.id} likes={item.likes} />
            </div>
            {item.description && <p className="mt-8 max-w-3xl whitespace-pre-line leading-relaxed text-slate-300">{item.description}</p>}
            <Comments mediaId={item.id} />
          </div>
          <div>
            <p className="eyebrow mb-4">Tags</p>
            <div className="flex flex-wrap gap-2" data-testid="video-tags">
              {(item.tags || []).length === 0 && <span className="text-sm text-slate-400">Sem tags</span>}
              {(item.tags || []).map((t) => (
                <Link key={t} to={`/videos?q=${encodeURIComponent(t)}`} className="border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:border-rose-500 hover:text-white" data-testid={`tag-${t}`}>#{t}</Link>
              ))}
            </div>
            <AdSlot placement="video_side" className="mt-10" />
          </div>
        </div>
      )}

      {related.length > 0 && (
        <section className="mt-24" data-testid="related-videos">
          <p className="eyebrow mb-3">Continue assistindo</p>
          <h2 className="font-serif-d mb-10 text-3xl text-white">Relacionados</h2>
          <div className="grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((v, i) => <Reveal key={v.id} delay={(i % 3) * 0.06}><MediaCard item={v} /></Reveal>)}
          </div>
        </section>
      )}
    </div>
  );
}
